// Keys whose values never reach Sentry (events, breadcrumbs, replay network bodies).
const SENSITIVE_KEYS = new Set([
  'token',
  'refreshtoken',
  'accesstoken',
  'authtoken',
  'authorization',
  'password',
  'password_confirmation',
  'currentpassword',
  'identitytoken',
  'authorizationcode',
  'rawnonce',
  'credential',
  'phoneverificationtoken',
  'fcmtoken',
]);

export const REDACTED = '[Filtered]';

const MAX_DEPTH = 8;

export const isSensitiveKey = (key: string): boolean => SENSITIVE_KEYS.has(key.toLowerCase());

const scrubString = (value: string): string =>
  value
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/g, `Bearer ${REDACTED}`)
    .replace(/\brt_[A-Za-z0-9_-]{20,}/g, REDACTED)
    .replace(/([?&](?:token|refreshToken)=)[^&#\s]+/gi, `$1${REDACTED}`);

// Returns a deep copy with sensitive values replaced; handles JSON strings (request/response bodies).
export const scrubSensitive = <T>(value: T, depth = 0): T => {
  if (depth > MAX_DEPTH || value === null || value === undefined) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length < 200_000) {
      try {
        return JSON.stringify(scrubSensitive(JSON.parse(trimmed), depth + 1)) as unknown as T;
      } catch {
        return scrubString(value) as unknown as T;
      }
    }
    return scrubString(value) as unknown as T;
  }
  if (Array.isArray(value)) return value.map((item) => scrubSensitive(item, depth + 1)) as unknown as T;
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] =
        isSensitiveKey(key) && item !== null && item !== undefined ? REDACTED : scrubSensitive(item, depth + 1);
    }
    return out as T;
  }
  return value;
};
